/**
 * Money formatting helper. Centralised so every price display in the app
 * renders the same way and so that future adjustments (locale-aware
 * currency, thousands separators, etc.) happen in one place.
 *
 * The app stores all money values as integer cents on the wire. Rendering
 * always produces `$X.XX` — even for round dollar amounts ($306 → `$306.00`).
 * The trailing zeros matter for clarity once percent-off coupons start
 * producing non-round totals (e.g. 33% off $306 = $204.18, displayed
 * alongside $170.00 for a 1-child Parent/Child Session — losing the
 * trailing zeros makes the comparison feel inconsistent).
 */

/**
 * Format an integer cents value as a USD string with two decimal places.
 *
 * Examples:
 * - `formatPriceUSD(17000)` → `$170.00`
 * - `formatPriceUSD(20418)` → `$204.18`
 * - `formatPriceUSD(0)` → `$0.00`
 *
 * Negative inputs are clamped to 0 (we never show a negative price).
 * Non-finite inputs (NaN, Infinity) render as `$0.00`.
 */
export function formatPriceUSD(cents: number): string {
  if (!Number.isFinite(cents) || cents < 0) return '$0.00';
  return `$${(cents / 100).toFixed(2)}`;
}
